/**
 * TrackRelationshipsTab
 *
 * Dedicated relationship review workspace for a track.
 * Shows every group/edge touching a pool item in one flat, filterable list.
 * Supports bulk Confirm / Dissolve and a side drawer for per-row detail.
 *
 * Constraint levels:
 *   blocking — hard dependency on an item outside the pool (unreviewed)
 *   warning  — any other unreviewed (candidate) relationship
 *   info     — already reviewed (active/confirmed) or purely informational
 *
 * Relationship types covered:
 *   Groups: package (prioritize/select/both), cluster (duplicate/variant), related_group, collection
 *   Edges:  dependency (hard/soft/informational), aggregation, related, elaboration
 */

import { useState, useMemo, useCallback, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  Circle,
  GitBranch,
  Link2,
  Plus,
  Check,
  X,
  ChevronRight,
  ArrowRightLeft,
  Package,
} from 'lucide-react';
import { useItemRelationshipsStore } from '@/stores/prioritization/itemRelationshipsStore';
import { useItemPoolsStore } from '@/stores/prioritization/itemPoolsStore';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import type {
  RelationshipStatus,
  RelationshipGroupType,
  RelationshipEdgeType,
  DependencySubtype,
  PackageSubtype,
  ClusterSubtype,
  ElaborationSubtype,
  GroupSubtype,
} from '@/types/prioritization/itemRelationships';
import type { Track } from '@/types/prioritization/index';
import { SlideOverPanelStandalone } from '@/components/panels/SlideOverPanel';

// ─── Shared inline style tokens (match TrackWorkspace's design language) ──────

const wsLabel: CSSProperties = { fontFamily: 'var(--mono)', fontSize: '0.55rem', letterSpacing: '0.14em', color: 'var(--text-dim)', textTransform: 'uppercase' };
const wsCard: CSSProperties = { border: '1px solid var(--rule)', background: 'var(--bg-raised)', borderRadius: '2px', padding: '14px' };
const wsGreenBtn = (disabled: boolean): CSSProperties => ({
  display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px',
  fontFamily: 'var(--mono)', fontSize: '0.6rem', letterSpacing: '0.06em', textTransform: 'uppercase',
  color: '#fff', background: 'var(--green)', border: 'none', borderRadius: '2px',
  cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
});
// ─── Types ────────────────────────────────────────────────────────────────────

type ConstraintLevel = 'blocking' | 'warning' | 'info';
type FilterKey = 'all' | 'needs_review' | 'packages' | 'dependencies' | 'aggregations' | 'other';

interface RelatedItem {
  id: string;
  title: string;
  inTrack: boolean;
  role?: string;
}

interface RelRow {
  id: string;
  kind: 'group' | 'edge';
  typeLabel: string;
  constraintLevel: ConstraintLevel;
  status: RelationshipStatus;
  /** True when this row contributes to the Relationships readiness blocker */
  needsReview: boolean;
  primaryItem: { id: string; title: string };
  relatedItems: RelatedItem[];
  /** Pool items involved in this relationship that are NOT in the current track */
  missingFromPool: Array<{ id: string; title: string }>;
  title?: string;
  rationale?: string;
  /** Human-readable hint about what this relationship means */
  contextHint: string;
  confirmLabel: string;
  filterKey: FilterKey;
  groupId?: string;
  edgeId?: string;
}

// ─── Label / hint helpers ─────────────────────────────────────────────────────

function groupTypeLabel(type: RelationshipGroupType, subtype?: GroupSubtype): string {
  if (type === 'package') {
    const sub = subtype as PackageSubtype | undefined;
    if (sub === 'prioritize') return 'Package — Rank together';
    if (sub === 'select') return 'Package — Select together';
    if (sub === 'prioritize_and_select') return 'Package — Rank & select';
    return 'Package';
  }
  if (type === 'cluster') {
    const sub = subtype as ClusterSubtype | undefined;
    if (sub === 'duplicate') return 'Cluster — Duplicate';
    if (sub === 'variant') return 'Cluster — Variant';
    return 'Cluster';
  }
  if (type === 'related_group') return 'Related group';
  if (type === 'collection') {
    if (subtype === 'pool') return 'Collection — Pool scope';
    if (subtype === 'curation') return 'Collection — Curation';
    return 'Collection';
  }
  return type;
}

function groupContextHint(type: RelationshipGroupType, subtype?: GroupSubtype): string {
  if (type === 'package') {
    const sub = subtype as PackageSubtype | undefined;
    if (sub === 'prioritize') return 'These items should be ranked as a unit — if one is in scope, all should be.';
    if (sub === 'select') return 'These items should be selected together or not at all.';
    if (sub === 'prioritize_and_select') return 'These items should be ranked and selected as a unit.';
    return 'These items form a package and should be handled together.';
  }
  if (type === 'cluster') {
    const sub = subtype as ClusterSubtype | undefined;
    if (sub === 'duplicate') return 'These items may be duplicates — consider consolidating before ranking.';
    if (sub === 'variant') return 'These items are variants of the same concept.';
    return 'These items are clustered.';
  }
  if (type === 'related_group') return 'These items are loosely related — informational context only.';
  if (type === 'collection') return 'These items share a collection scope.';
  return 'These items are grouped.';
}

function edgeTypeLabel(type: RelationshipEdgeType, subtype?: DependencySubtype | ElaborationSubtype): string {
  if (type === 'dependency') {
    const sub = subtype as DependencySubtype | undefined;
    if (sub === 'hard') return 'Hard dependency';
    if (sub === 'soft') return 'Soft dependency';
    if (sub === 'informational') return 'Informational link';
    return 'Dependency';
  }
  if (type === 'aggregation') return 'Aggregation';
  if (type === 'related') return 'Related';
  if (type === 'elaboration') {
    const sub = subtype as ElaborationSubtype | undefined;
    if (sub === 'solution_spectrum') return 'Elaboration — Spectrum';
    if (sub === 'solution_variant') return 'Elaboration — Variant';
    return 'Elaboration';
  }
  return type;
}

function edgeContextHint(
  type: RelationshipEdgeType,
  subtype?: DependencySubtype | ElaborationSubtype,
  otherInTrack?: boolean,
): string {
  if (type === 'dependency') {
    const sub = subtype as DependencySubtype | undefined;
    if (sub === 'hard') {
      return otherInTrack
        ? 'Hard dependency — both items are in scope, ranking order matters.'
        : 'Hard dependency on an item outside this track — resolve before ranking or add the dependency to the pool.';
    }
    if (sub === 'soft') return 'Soft dependency — consider whether both items should be in scope together.';
    if (sub === 'informational') return 'Informational link — no enforcement weight.';
    return 'This item has a dependency on another item.';
  }
  if (type === 'aggregation')
    return 'Aggregation relationship — one item encompasses or is part of the other.';
  if (type === 'related') return 'General association — informational context only.';
  if (type === 'elaboration') return 'Elaboration relationship — these items refine or expand on each other.';
  return 'These items are connected by a relationship.';
}

function edgeConfirmLabel(type: RelationshipEdgeType, subtype?: DependencySubtype | ElaborationSubtype): string {
  if (type === 'dependency' && subtype === 'hard') return 'Acknowledge dependency';
  if (type === 'dependency') return 'Confirm link';
  if (type === 'aggregation') return 'Confirm aggregation';
  return 'Confirm relationship';
}

function groupConfirmLabel(type: RelationshipGroupType): string {
  if (type === 'package') return 'Accept package';
  if (type === 'cluster') return 'Confirm cluster';
  return 'Confirm group';
}

function edgeFilterKey(type: RelationshipEdgeType): FilterKey {
  if (type === 'dependency') return 'dependencies';
  if (type === 'aggregation') return 'aggregations';
  return 'other';
}

// ─── Status chip ──────────────────────────────────────────────────────────────

const chipBase: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: '4px', borderRadius: '2px', padding: '2px 8px', fontSize: '0.7rem', fontWeight: 500 };

function StatusChip({ status }: { status: RelationshipStatus }) {
  if (status === 'candidate') {
    return (
      <span style={{ ...chipBase, background: 'rgba(212,160,23,0.12)', color: 'var(--mustard)' }}>
        <Circle size={9} />
        Unreviewed
      </span>
    );
  }
  if (status === 'active' || status === 'confirmed') {
    return (
      <span className="dark:!text-[var(--text-ter)]" style={{ ...chipBase, background: 'rgba(58,125,86,0.12)', color: 'var(--green)' }}>
        <CheckCircle2 size={9} />
        {status === 'confirmed' ? 'Confirmed' : 'Active'}
      </span>
    );
  }
  if (status === 'dissolved' || status === 'dismissed') {
    return (
      <span style={{ ...chipBase, background: 'var(--hover-tint)', color: 'var(--text-sec)' }}>
        <X size={9} />
        {status === 'dissolved' ? 'Dissolved' : 'Dismissed'}
      </span>
    );
  }
  return <span style={{ ...chipBase, background: 'var(--hover-tint)', color: 'var(--text-sec)' }}>{status}</span>;
}

// ─── Constraint level icon ────────────────────────────────────────────────────

function LevelIcon({ level }: { level: ConstraintLevel }) {
  if (level === 'blocking')
    return <AlertCircle size={15} style={{ flexShrink: 0, color: 'var(--coral)' }} />;
  if (level === 'warning')
    return <AlertTriangle size={15} style={{ flexShrink: 0, color: 'var(--mustard)' }} />;
  return <Circle size={15} style={{ flexShrink: 0, color: 'var(--text-dim)' }} />;
}

// ─── Main component ───────────────────────────────────────────────────────────

interface TrackRelationshipsTabProps {
  track: Track;
  poolItems: Array<{ id: string; title?: string }>;
  trackPool: { id: string; poolLockedAt?: Date | null } | undefined;
}

export function TrackRelationshipsTab({ track, poolItems, trackPool }: TrackRelationshipsTabProps) {
  const rawAllItems = useItemsStore((s) => s.items);
  const allItems = useMemo(() => rawAllItems || [], [rawAllItems]);
  const rawGroups = useItemRelationshipsStore((s) => s.groups);
  const groups = useMemo(() => rawGroups || [], [rawGroups]);
  const rawMemberships = useItemRelationshipsStore((s) => s.groupMemberships);
  const memberships = useMemo(() => rawMemberships || [], [rawMemberships]);
  const rawEdges = useItemRelationshipsStore((s) => s.edges);
  const edges = useMemo(() => rawEdges || [], [rawEdges]);
  const updateGroup = useItemRelationshipsStore((s) => s.updateGroup);
  const dissolveGroup = useItemRelationshipsStore((s) => s.dissolveGroup);
  const updateEdge = useItemRelationshipsStore((s) => s.updateEdge);
  const dissolveEdge = useItemRelationshipsStore((s) => s.dissolveEdge);
  const addPriorityToList = useItemPoolsStore((s) => s.addPriorityToList);
  const userId = useUserStore((s) => s.userId) ?? 'system';

  const poolLocked = !!trackPool?.poolLockedAt;

  const [filter, setFilter] = useState<FilterKey>('needs_review');
  const [showDissolved, setShowDissolved] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [drawerRow, setDrawerRow] = useState<RelRow | null>(null);

  // ── Build flat list ────────────────────────────────────────────────────────

  const poolItemIds = useMemo(() => new Set(poolItems.map((i) => i.id)), [poolItems]);

  const itemTitleById = useMemo(
    () => new Map(allItems.map((i) => [i.id, i.title ?? i.id])),
    [allItems],
  );
  const poolItemTitle = useCallback(
    (id: string) =>
      poolItems.find((i) => i.id === id)?.title ?? itemTitleById.get(id) ?? id,
    [poolItems, itemTitleById],
  );

  const rows = useMemo<RelRow[]>(() => {
    const result: RelRow[] = [];
    const seenGroupIds = new Set<string>();
    const seenEdgeIds = new Set<string>();

    // ── Groups ────────────────────────────────────────────────────────────────
    for (const group of groups) {
      if (seenGroupIds.has(group.id)) continue;
      const groupMembers = memberships
        .filter((m) => m.groupId === group.id)
        .map((m) => ({
          id: m.itemId,
          title: itemTitleById.get(m.itemId) ?? m.itemId,
          inTrack: poolItemIds.has(m.itemId),
          role: m.membershipRole,
        }));

      // Only include if at least one member is in this track's pool
      if (!groupMembers.some((m) => m.inTrack)) continue;
      seenGroupIds.add(group.id);

      const missingFromPool = groupMembers.filter((m) => !m.inTrack);
      const primaryMember =
        groupMembers.find((m) => m.inTrack && m.role === 'canonical') ??
        groupMembers.find((m) => m.inTrack) ??
        groupMembers[0];

      const isPackageWithMissing = group.type === 'package' && missingFromPool.length > 0;
      const isCandidate = group.status === 'candidate';

      // Only a package with missing members is a real violation.
      // A candidate relationship whose scope is fully satisfied is informational only.
      const constraintLevel: ConstraintLevel =
        isPackageWithMissing
          ? 'blocking'
          : isCandidate
            ? 'warning'
            : 'info';

      result.push({
        id: group.id,
        kind: 'group',
        typeLabel: groupTypeLabel(group.type, group.subtype),
        constraintLevel,
        status: group.status,
        needsReview: isPackageWithMissing,
        primaryItem: { id: primaryMember.id, title: primaryMember.title },
        relatedItems: groupMembers.filter((m) => m.id !== primaryMember.id),
        missingFromPool,
        title: group.title,
        rationale: group.rationale,
        contextHint: groupContextHint(group.type, group.subtype),
        confirmLabel: groupConfirmLabel(group.type),
        filterKey: group.type === 'package' ? 'packages' : 'other',
        groupId: group.id,
      });
    }

    // ── Edges ─────────────────────────────────────────────────────────────────
    for (const edge of edges) {
      if (seenEdgeIds.has(edge.id)) continue;
      const srcInPool = poolItemIds.has(edge.sourceItemId);
      const tgtInPool = poolItemIds.has(edge.targetItemId);
      if (!srcInPool && !tgtInPool) continue;
      seenEdgeIds.add(edge.id);

      const primaryId = srcInPool ? edge.sourceItemId : edge.targetItemId;
      const otherId = srcInPool ? edge.targetItemId : edge.sourceItemId;
      const otherInPool = poolItemIds.has(otherId);

      const isHardDepOutside =
        edge.type === 'dependency' &&
        (edge.subtype as DependencySubtype) === 'hard' &&
        !otherInPool;

      const isCandidate = edge.status === 'candidate';

      // Hard dep outside the pool is the only edge-level scope violation.
      // A candidate (unreviewed) relationship that doesn't break scope is just a warning.
      const constraintLevel: ConstraintLevel =
        isHardDepOutside
          ? 'blocking'
          : isCandidate
            ? 'warning'
            : 'info';

      result.push({
        id: edge.id,
        kind: 'edge',
        typeLabel: edgeTypeLabel(edge.type, edge.subtype),
        constraintLevel,
        status: edge.status,
        needsReview: isHardDepOutside,
        primaryItem: {
          id: primaryId,
          title: poolItemTitle(primaryId),
        },
        relatedItems: [
          {
            id: otherId,
            title: itemTitleById.get(otherId) ?? otherId,
            inTrack: otherInPool,
          },
        ],
        missingFromPool: otherInPool ? [] : [{ id: otherId, title: itemTitleById.get(otherId) ?? otherId }],
        rationale: edge.rationale,
        contextHint: edgeContextHint(edge.type, edge.subtype, otherInPool),
        confirmLabel: edgeConfirmLabel(edge.type, edge.subtype),
        filterKey: edgeFilterKey(edge.type),
        edgeId: edge.id,
      });
    }

    // Sort: blocking → warning → info; within each tier: candidate first
    const levelOrder: Record<ConstraintLevel, number> = { blocking: 0, warning: 1, info: 2 };
    result.sort((a, b) => {
      const lvDiff = levelOrder[a.constraintLevel] - levelOrder[b.constraintLevel];
      if (lvDiff !== 0) return lvDiff;
      const aNeedsReview = a.status === 'candidate' ? 0 : 1;
      const bNeedsReview = b.status === 'candidate' ? 0 : 1;
      return aNeedsReview - bNeedsReview;
    });

    return result;
  }, [groups, memberships, edges, poolItemIds, itemTitleById, poolItemTitle]);

  // ── Filter counts ──────────────────────────────────────────────────────────

  const activeRows = useMemo(
    () => showDissolved ? rows : rows.filter((r) => r.status !== 'dissolved' && r.status !== 'dismissed'),
    [rows, showDissolved],
  );

  const filterCounts = useMemo<Record<FilterKey, number>>(
    () => ({
      all: activeRows.length,
      needs_review: activeRows.filter((r) => r.needsReview).length,
      packages: activeRows.filter((r) => r.filterKey === 'packages').length,
      dependencies: activeRows.filter((r) => r.filterKey === 'dependencies').length,
      aggregations: activeRows.filter((r) => r.filterKey === 'aggregations').length,
      other: activeRows.filter((r) => !['packages', 'dependencies', 'aggregations'].includes(r.filterKey)).length,
    }),
    [activeRows],
  );

  const visibleRows = useMemo(() => {
    if (filter === 'all') return activeRows;
    if (filter === 'needs_review') return activeRows.filter((r) => r.needsReview);
    return activeRows.filter((r) => r.filterKey === filter);
  }, [activeRows, filter]);

  // ── Selection ──────────────────────────────────────────────────────────────

  const toggleRow = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    const visibleIds = visibleRows.map((r) => r.id);
    const allSelected = visibleIds.every((id) => selected.has(id));
    if (allSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        visibleIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        visibleIds.forEach((id) => next.add(id));
        return next;
      });
    }
  }, [visibleRows, selected]);

  const selectedRows = useMemo(
    () => rows.filter((r) => selected.has(r.id)),
    [rows, selected],
  );

  // ── Bulk actions ───────────────────────────────────────────────────────────

  const handleBulkConfirm = useCallback(() => {
    for (const row of selectedRows) {
      if (row.groupId) updateGroup(row.groupId, { status: 'active' });
      else if (row.edgeId) updateEdge(row.edgeId, { status: 'active' });
    }
    setSelected(new Set());
  }, [selectedRows, updateGroup, updateEdge]);

  const handleBulkDissolve = useCallback(() => {
    for (const row of selectedRows) {
      if (row.groupId) dissolveGroup(row.groupId);
      else if (row.edgeId) dissolveEdge(row.edgeId);
    }
    setSelected(new Set());
  }, [selectedRows, dissolveGroup, dissolveEdge]);

  // ── Per-row actions ────────────────────────────────────────────────────────

  const handleConfirmRow = useCallback(
    (row: RelRow) => {
      if (row.groupId) updateGroup(row.groupId, { status: 'active' });
      else if (row.edgeId) updateEdge(row.edgeId, { status: 'active' });
      if (drawerRow?.id === row.id) setDrawerRow(null);
    },
    [updateGroup, updateEdge, drawerRow],
  );

  const handleDissolveRow = useCallback(
    (row: RelRow) => {
      if (row.groupId) dissolveGroup(row.groupId);
      else if (row.edgeId) dissolveEdge(row.edgeId);
      if (drawerRow?.id === row.id) setDrawerRow(null);
    },
    [dissolveGroup, dissolveEdge, drawerRow],
  );

  const handleAddToPool = useCallback(
    (item: { id: string }) => {
      if (!trackPool) return;
      addPriorityToList(trackPool.id, item.id, track.cycleId ?? undefined, userId);
    },
    [trackPool, addPriorityToList, track.cycleId, userId],
  );

  // ── Row type badge ─────────────────────────────────────────────────────────

  function RowTypeBadge({ row }: { row: RelRow }) {
    const isPackage = row.kind === 'group' && row.filterKey === 'packages';
    const isDependency = row.filterKey === 'dependencies';
    const color = isPackage ? 'var(--slate)' : isDependency ? 'var(--mustard)' : 'var(--text-sec)';
    const bg = isPackage ? 'rgba(61,75,111,0.1)' : isDependency ? 'rgba(212,160,23,0.12)' : 'var(--hover-tint)';
    const Icon =
      row.kind === 'group'
        ? GitBranch
        : row.filterKey === 'dependencies'
          ? ArrowRightLeft
          : Link2;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', borderRadius: '2px', padding: '2px 8px', fontSize: '0.7rem', fontWeight: 500, color, background: bg }}>
        <Icon size={11} />
        {row.typeLabel}
      </span>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  const FILTERS: Array<{ key: FilterKey; label: string }> = [
    { key: 'needs_review', label: 'Needs review' },
    { key: 'all', label: 'All' },
    { key: 'packages', label: 'Packages' },
    { key: 'dependencies', label: 'Dependencies' },
    { key: 'aggregations', label: 'Aggregations' },
    { key: 'other', label: 'Other' },
  ];

  const allVisibleSelected =
    visibleRows.length > 0 && visibleRows.every((r) => selected.has(r.id));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Filter bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', overflowX: 'auto' }}>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => { setFilter(f.key); setSelected(new Set()); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px',
                fontFamily: 'var(--mono)', fontSize: '0.6rem', letterSpacing: '0.06em', textTransform: 'uppercase',
                whiteSpace: 'nowrap', border: 'none', borderRadius: '2px', cursor: 'pointer',
                color: filter === f.key ? '#fff' : 'var(--text-sec)',
                background: filter === f.key ? 'var(--mustard)' : 'var(--hover-tint)',
              }}
            >
              {f.label}
              {filterCounts[f.key] > 0 && (
                <span
                  style={{
                    borderRadius: '2px', padding: '1px 6px', fontSize: '0.65rem',
                    background: filter === f.key ? 'rgba(255,255,255,0.25)' : 'var(--bg-raised)',
                    color: filter === f.key ? '#fff' : 'var(--text-sec)',
                  }}
                >
                  {filterCounts[f.key]}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Show dissolved toggle + bulk actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <button
            type="button"
            onClick={() => setShowDissolved((v) => !v)}
            style={{
              padding: '7px 12px', fontFamily: 'var(--mono)', fontSize: '0.6rem', letterSpacing: '0.06em', textTransform: 'uppercase',
              whiteSpace: 'nowrap', border: 'none', borderRadius: '2px', cursor: 'pointer',
              color: showDissolved ? 'var(--text-pri)' : 'var(--text-dim)',
              background: showDissolved ? 'var(--hover-tint)' : 'none',
            }}
          >
            {showDissolved ? 'Hide dissolved' : 'Show dissolved'}
          </button>
        </div>

        {/* Bulk actions — visible when rows are selected */}
        {selected.size > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>{selected.size} selected</span>
            <button
              type="button"
              onClick={handleBulkConfirm}
              className="dark:!text-[var(--text-ter)]"
              style={{ display: 'flex', alignItems: 'center', gap: '6px', borderRadius: '2px', border: '1px solid rgba(58,125,86,0.3)', background: 'rgba(58,125,86,0.1)', padding: '6px 12px', fontSize: '0.75rem', fontWeight: 600, color: 'var(--green)', cursor: 'pointer' }}
            >
              <Check size={13} />
              Confirm all
            </button>
            <button
              type="button"
              onClick={handleBulkDissolve}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', borderRadius: '2px', border: '1px solid var(--rule)', background: 'var(--hover-tint)', padding: '6px 12px', fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-sec)', cursor: 'pointer' }}
            >
              <X size={13} />
              Dissolve all
            </button>
          </div>
        )}
      </div>

      {/* Empty state */}
      {visibleRows.length === 0 && (
        <div style={{ border: '1px dashed var(--rule)', borderRadius: '2px', padding: '64px 0', textAlign: 'center' }}>
          <CheckCircle2 size={36} style={{ margin: '0 auto 12px', color: 'var(--green)' }} />
          <p style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-pri)' }}>
            {filter === 'needs_review'
              ? 'No scope violations — all relationships are satisfied.'
              : 'No relationships in this category.'}
          </p>
          {filter !== 'all' && (
            <button
              type="button"
              onClick={() => setFilter('all')}
              style={{ marginTop: '8px', fontSize: '0.75rem', color: 'var(--text-ter)', background: 'none', border: 'none', textDecoration: 'underline', cursor: 'pointer' }}
            >
              Show all relationships
            </button>
          )}
        </div>
      )}

      {/* List */}
      {visibleRows.length > 0 && (
        <div style={{ border: '1px solid var(--rule)', borderRadius: '2px', overflow: 'hidden' }}>
          {/* Table header */}
          <div style={{ display: 'grid', gridTemplateColumns: '2rem 1fr 1fr 8rem 7rem auto', alignItems: 'center', gap: '12px', background: 'var(--hover-tint)', padding: '10px 16px', borderBottom: '1px solid var(--rule)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} />
            </div>
            <div style={wsLabel}>Pool item</div>
            <div style={wsLabel}>Relationship / With</div>
            <div style={wsLabel}>Status</div>
            <div style={wsLabel}>Constraint</div>
            <div />
          </div>

          {/* Rows */}
          {visibleRows.map((row, i) => {
            const rowBg =
              row.constraintLevel === 'blocking'
                ? 'rgba(192,83,58,0.05)'
                : row.constraintLevel === 'warning'
                  ? 'rgba(212,160,23,0.05)'
                  : 'transparent';

            return (
              <div
                key={row.id}
                style={{
                  display: 'grid', gridTemplateColumns: '2rem 1fr 1fr 8rem 7rem auto', alignItems: 'flex-start', gap: '12px',
                  padding: '14px 16px', borderTop: i === 0 ? 'none' : '1px solid var(--rule)', cursor: 'pointer', background: rowBg,
                }}
                onClick={() => setDrawerRow(row)}
              >
                {/* Checkbox */}
                <div
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '2px' }}
                  onClick={(e) => { e.stopPropagation(); toggleRow(row.id); }}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(row.id)}
                    onChange={() => toggleRow(row.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>

                {/* Pool item */}
                <div style={{ minWidth: 0 }}>
                  <Link
                    to={`/prioritization/items?id=${row.primaryItem.id}`}
                    style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-pri)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {row.primaryItem.title}
                  </Link>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.contextHint}</p>
                </div>

                {/* Relationship + with */}
                <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <RowTypeBadge row={row} />
                  {row.title && (
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-sec)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.title}</p>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {row.relatedItems.slice(0, 3).map((item) => (
                      <Link
                        key={item.id}
                        to={`/prioritization/items?id=${item.id}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          display: 'inline-flex', alignItems: 'center', borderRadius: '2px', border: '1px solid',
                          padding: '2px 8px', fontSize: '0.7rem',
                          borderColor: item.inTrack ? 'rgba(58,125,86,0.35)' : 'rgba(192,83,58,0.35)',
                          background: item.inTrack ? 'rgba(58,125,86,0.08)' : 'rgba(192,83,58,0.08)',
                          color: item.inTrack ? 'var(--green)' : 'var(--coral)',
                        }}
                      >
                        {item.title}
                        {item.role ? ` · ${item.role}` : ''}
                        {!item.inTrack && ' · not in pool'}
                      </Link>
                    ))}
                    {row.relatedItems.length > 3 && (
                      <span style={{ fontSize: '0.7rem', color: 'var(--text-dim)' }}>+{row.relatedItems.length - 3} more</span>
                    )}
                  </div>
                </div>

                {/* Status */}
                <div style={{ paddingTop: '2px' }}>
                  <StatusChip status={row.status} />
                </div>

                {/* Constraint level */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', paddingTop: '2px' }}>
                  <LevelIcon level={row.constraintLevel} />
                  <span style={{
                    fontSize: '0.75rem', fontWeight: 500, textTransform: 'capitalize',
                    color: row.constraintLevel === 'blocking' ? 'var(--coral)' : row.constraintLevel === 'warning' ? 'var(--mustard)' : 'var(--text-dim)',
                  }}>
                    {row.constraintLevel}
                  </span>
                </div>

                {/* Quick actions */}
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {row.status === 'candidate' && (
                    <>
                      <button
                        type="button"
                        title={row.confirmLabel}
                        onClick={() => handleConfirmRow(row)}
                        style={{ padding: '6px', borderRadius: '2px', background: 'rgba(58,125,86,0.1)', color: 'var(--green)', border: '1px solid rgba(58,125,86,0.3)', cursor: 'pointer' }}
                      >
                        <Check size={13} />
                      </button>
                      <button
                        type="button"
                        title="Dissolve"
                        onClick={() => handleDissolveRow(row)}
                        style={{ padding: '6px', borderRadius: '2px', background: 'var(--hover-tint)', color: 'var(--text-sec)', border: '1px solid var(--rule)', cursor: 'pointer' }}
                      >
                        <X size={13} />
                      </button>
                    </>
                  )}
                  <ChevronRight size={15} style={{ color: 'var(--text-dim)' }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail drawer */}
      <SlideOverPanelStandalone
        open={!!drawerRow}
        onClose={() => setDrawerRow(null)}
        title={drawerRow?.typeLabel ?? 'Relationship detail'}
        size="xl"
        footer={
          drawerRow ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Link
                  to={`/prioritization/items?id=${drawerRow.primaryItem.id}`}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px', fontFamily: 'var(--mono)', fontSize: '0.6rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-sec)', background: 'none', border: '1px solid var(--rule)', borderRadius: '2px', textDecoration: 'none' }}
                  onClick={() => setDrawerRow(null)}
                >
                  Open item
                </Link>
                {drawerRow.status === 'candidate' && (
                  <>
                    <button
                      type="button"
                      onClick={() => handleConfirmRow(drawerRow)}
                      style={wsGreenBtn(false)}
                    >
                      <Check size={13} />
                      {drawerRow.confirmLabel}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDissolveRow(drawerRow)}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px', fontFamily: 'var(--mono)', fontSize: '0.6rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-sec)', background: 'none', border: '1px solid var(--rule)', borderRadius: '2px', cursor: 'pointer' }}
                    >
                      <X size={13} />
                      Dissolve
                    </button>
                  </>
                )}
              </div>
              <button
                type="button"
                onClick={() => setDrawerRow(null)}
                style={wsGreenBtn(false)}
              >
                Done
              </button>
            </div>
          ) : null
        }
      >
        {drawerRow && <DrawerContent row={drawerRow} trackPool={trackPool} poolLocked={poolLocked} onAddToPool={handleAddToPool} onClose={() => setDrawerRow(null)} onConfirm={handleConfirmRow} onDissolve={handleDissolveRow} />}
      </SlideOverPanelStandalone>
    </div>
  );
}

// ─── Drawer content ───────────────────────────────────────────────────────────

function DrawerContent({
  row,
  trackPool,
  poolLocked,
  onAddToPool,
  onClose: _onClose,
  onConfirm: _onConfirm,
  onDissolve: _onDissolve,
}: {
  row: RelRow;
  trackPool: { id: string } | undefined;
  poolLocked: boolean;
  onAddToPool: (item: { id: string; title: string }) => void;
  onClose: () => void;
  onConfirm: (row: RelRow) => void;
  onDissolve: (row: RelRow) => void;
}) {
  const levelTint = row.constraintLevel === 'blocking' ? 'rgba(192,83,58,0.08)' : row.constraintLevel === 'warning' ? 'rgba(212,160,23,0.08)' : 'var(--hover-tint)';
  const levelBorder = row.constraintLevel === 'blocking' ? 'rgba(192,83,58,0.3)' : row.constraintLevel === 'warning' ? 'rgba(212,160,23,0.3)' : 'var(--rule)';
  const levelText = row.constraintLevel === 'blocking' ? 'var(--coral)' : row.constraintLevel === 'warning' ? 'var(--mustard)' : 'var(--text-pri)';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Context hint */}
      <div style={{ borderRadius: '2px', border: `1px solid ${levelBorder}`, background: levelTint, padding: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
          <LevelIcon level={row.constraintLevel} />
          <div>
            <p style={{ fontSize: '0.85rem', fontWeight: 500, color: levelText }}>
              {row.constraintLevel === 'blocking'
                ? 'Action required before ranking'
                : row.constraintLevel === 'warning'
                  ? 'Review recommended before ranking'
                  : 'Informational — no action required'}
            </p>
            <p style={{ marginTop: '4px', fontSize: '0.75rem', color: 'var(--text-sec)' }}>{row.contextHint}</p>
          </div>
        </div>
      </div>

      {/* Status + badges */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
        <StatusChip status={row.status} />
        {row.kind === 'group' && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', borderRadius: '2px', background: 'rgba(61,75,111,0.1)', padding: '2px 8px', fontSize: '0.7rem', fontWeight: 500, color: 'var(--text-ter)' }}>
            <GitBranch size={11} />
            {row.typeLabel}
          </span>
        )}
        {row.kind === 'edge' && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', borderRadius: '2px', background: 'rgba(212,160,23,0.12)', padding: '2px 8px', fontSize: '0.7rem', fontWeight: 500, color: 'var(--mustard)' }}>
            <ArrowRightLeft size={11} />
            {row.typeLabel}
          </span>
        )}
      </div>

      {/* Title / rationale */}
      {row.title && (
        <div>
          <p style={{ ...wsLabel, marginBottom: '4px' }}>Title</p>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-pri)' }}>{row.title}</p>
        </div>
      )}
      {row.rationale && (
        <div>
          <p style={{ ...wsLabel, marginBottom: '4px' }}>Rationale</p>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-sec)', fontStyle: 'italic' }}>"{row.rationale}"</p>
        </div>
      )}

      {/* All involved items */}
      <div>
        <p style={{ ...wsLabel, marginBottom: '8px' }}>
          {row.kind === 'group' ? 'Members' : 'Items'}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {[row.primaryItem, ...row.relatedItems].map((item) => {
            const inTrack = 'inTrack' in item ? item.inTrack : true;
            const role = 'role' in item ? item.role : undefined;
            return (
              <div
                key={item.id}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', borderRadius: '2px', padding: '10px 12px',
                  border: `1px solid ${inTrack ? 'rgba(58,125,86,0.25)' : 'rgba(192,83,58,0.25)'}`,
                  background: inTrack ? 'rgba(58,125,86,0.05)' : 'rgba(192,83,58,0.05)',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <Link
                    to={`/prioritization/items?id=${item.id}`}
                    style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-pri)' }}
                  >
                    {item.title}
                  </Link>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: '2px' }}>
                    {inTrack ? '✓ In pool' : '⚠ Not in this track\'s pool'}
                    {role ? ` · ${role}` : ''}
                  </p>
                </div>
                {!inTrack && trackPool && !poolLocked && (
                  <button
                    type="button"
                    onClick={() => onAddToPool(item)}
                    style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0, padding: '6px 10px', borderRadius: '2px', border: '1px solid rgba(61,75,111,0.3)', background: 'rgba(61,75,111,0.08)', fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-ter)', cursor: 'pointer' }}
                  >
                    <Plus size={13} />
                    Add to pool
                  </button>
                )}
                {!inTrack && poolLocked && (
                  <span style={{ fontSize: '0.7rem', color: 'var(--text-dim)', fontStyle: 'italic', flexShrink: 0 }}>Pool locked</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* What each action means */}
      <div style={{ ...wsCard, display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <p style={wsLabel}>What does each action do?</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.8rem', color: 'var(--text-sec)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <Check size={14} style={{ color: 'var(--green)', marginTop: '2px', flexShrink: 0 }} />
            <span>
              <span style={{ fontWeight: 500, color: 'var(--text-pri)' }}>{row.confirmLabel}</span>
              {' — '}
              {row.kind === 'group' && row.filterKey === 'packages'
                ? 'Marks the package as acknowledged. The group stays active in the system and will inform decisions.'
                : row.filterKey === 'dependencies'
                  ? 'Marks the dependency as acknowledged and active. The relationship is preserved.'
                  : 'Marks this relationship as confirmed and active.'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <X size={14} style={{ color: 'var(--text-dim)', marginTop: '2px', flexShrink: 0 }} />
            <span>
              <span style={{ fontWeight: 500, color: 'var(--text-pri)' }}>Dissolve</span>
              {' — '}
              {row.kind === 'group' && row.filterKey === 'packages'
                ? 'Breaks the package entirely. Items can then be ranked independently.'
                : 'Removes this relationship. The items are no longer linked.'}
            </span>
          </div>
          {row.missingFromPool.length > 0 && trackPool && !poolLocked && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <Package size={14} style={{ color: 'var(--text-ter)', marginTop: '2px', flexShrink: 0 }} />
              <span>
                <span style={{ fontWeight: 500, color: 'var(--text-pri)' }}>Add to pool</span>
                {' — '}
                Adds the missing member(s) to this track's pool so the relationship is complete.
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
